import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { CourseWithUnits, ChatSummary, Unit, Lesson, ExternalLink, AssetMaterial, ExamSummary } from '../../types';
import { parseCourseMetadataInput } from '../../utils/courseMetadata';
import Navbar from '../shared/Navbar';

// Natural sort key for tiebreaker
function naturalSortKey(text: string): (string | number)[] {
  return text.split(/(\d+)/).map(part => {
    const num = parseInt(part, 10);
    return isNaN(num) ? part.toLowerCase() : num;
  });
}

function compareNatural(a: string, b: string): number {
  const aKey = naturalSortKey(a);
  const bKey = naturalSortKey(b);
  for (let i = 0; i < Math.max(aKey.length, bKey.length); i++) {
    const aVal = aKey[i] ?? '';
    const bVal = bKey[i] ?? '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      if (aVal !== bVal) return aVal - bVal;
    } else {
      const cmp = String(aVal).localeCompare(String(bVal));
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

function chatTimestamp(value?: string | null): number {
  if (!value) return 0;
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const timestamp = new Date(normalized).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function formatChatTimestamp(value?: string | null): string {
  if (!value) return 'Not completed';
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

type UnitItem = 
  | { type: 'lesson'; data: Lesson }
  | { type: 'link'; data: ExternalLink }
  | { type: 'exam'; data: ExamSummary }
  | { type: 'asset_material'; data: AssetMaterial };

const ManageCoursePage: React.FC = () => {
  const { courseId: courseIdParam } = useParams<{ courseId: string }>();
  const courseId = courseIdParam ? parseInt(courseIdParam, 10) : null;

  const [course, setCourse] = useState<CourseWithUnits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<{ authenticated: boolean; user?: { email: string; is_admin: boolean } } | null>(null);

  // Editing course properties
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    detailed_description: '',
    subjects: '',
    levels: '',
    tags: '',
    hidden: false,
  });
  const [saving, setSaving] = useState(false);

  // Unit management
  const [addingUnit, setAddingUnit] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [editingUnitId, setEditingUnitId] = useState<number | null>(null);
  const [editUnitName, setEditUnitName] = useState('');
  const [unitBusy, setUnitBusy] = useState<Record<number, boolean>>({});

  // Lesson management
  const [deleting, setDeleting] = useState<string | null>(null);
  const [lessonChats, setLessonChats] = useState<Record<string, ChatSummary[]>>({});
  const [chatsLoading, setChatsLoading] = useState<Record<string, boolean>>({});
  const [expandedLessons, setExpandedLessons] = useState<Record<string, boolean>>({});
  const [toggling, setToggling] = useState<Record<string, boolean>>({});
  const [movingLesson, setMovingLesson] = useState<{ lessonId: string; lessonTitle: string } | null>(null);

  // External link management
  const [addingLinkToUnit, setAddingLinkToUnit] = useState<number | null>(null);
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [editingLink, setEditingLink] = useState<ExternalLink | null>(null);
  const [editLinkName, setEditLinkName] = useState('');
  const [editLinkUrl, setEditLinkUrl] = useState('');
  const [linkBusy, setLinkBusy] = useState<Record<number, boolean>>({});

  // Order index editing
  const [editingOrderLesson, setEditingOrderLesson] = useState<string | null>(null);
  const [editingOrderLink, setEditingOrderLink] = useState<number | null>(null);
  const [editingOrderAssetMat, setEditingOrderAssetMat] = useState<number | null>(null);
  const [editOrderValue, setEditOrderValue] = useState<string>('');

  // Asset material management
  const [addingAssetMatToUnit, setAddingAssetMatToUnit] = useState<number | null>(null);
  const [newAssetMatName, setNewAssetMatName] = useState('');
  const [newAssetMatAssetId, setNewAssetMatAssetId] = useState('');
  const [editingAssetMat, setEditingAssetMat] = useState<AssetMaterial | null>(null);
  const [editAssetMatName, setEditAssetMatName] = useState('');
  const [assetMatBusy, setAssetMatBusy] = useState<Record<number, boolean>>({});
  const [courseAssets, setCourseAssets] = useState<any[]>([]);

  const loadCourse = useCallback(async () => {
    if (!courseId) return;
    try {
      setLoading(true);
      const courseData = await api.getCourse(courseId);
      setCourse(courseData);
      setEditForm({
        title: courseData.title,
        description: courseData.description,
        detailed_description: courseData.detailed_description,
        subjects: (courseData.subjects || []).join(', '),
        levels: (courseData.levels || []).join(', '),
        tags: (courseData.tags || []).join(', '),
        hidden: courseData.hidden,
      });
      // Load course assets for asset material selection
      try {
        const assetsRes = await api.getCourseAssets(courseId);
        setCourseAssets(assetsRes.assets || []);
      } catch { /* ignore if not admin */ }
    } catch (e) {
      setError('Failed to load course');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const current = await api.me();
        setMe(current);
        if (!(current?.authenticated && current?.user?.is_admin)) {
          setLoading(false);
          return;
        }
        await loadCourse();
      } catch (e) {
        setError('Failed to load course');
        console.error(e);
      }
    };
    void bootstrap();
  }, [loadCourse]);

  const handleSave = async () => {
    if (!courseId || !editForm.title.trim()) {
      alert('Title is required');
      return;
    }
    try {
      setSaving(true);
      await api.updateCourse(courseId, {
        ...editForm,
        subjects: parseCourseMetadataInput(editForm.subjects),
        levels: parseCourseMetadataInput(editForm.levels),
        tags: parseCourseMetadataInput(editForm.tags),
      });
      await loadCourse();
      setEditing(false);
    } catch (e: any) {
      alert(e?.message || 'Failed to update course');
    } finally {
      setSaving(false);
    }
  };

  // Unit handlers
  const handleAddUnit = async () => {
    if (!courseId || !newUnitName.trim()) return;
    try {
      setAddingUnit(true);
      await api.createUnit(courseId, newUnitName.trim());
      setNewUnitName('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to create unit');
    } finally {
      setAddingUnit(false);
    }
  };

  const handleRenameUnit = async (unitId: number) => {
    if (!editUnitName.trim()) return;
    try {
      setUnitBusy(prev => ({ ...prev, [unitId]: true }));
      await api.updateUnit(unitId, { name: editUnitName.trim() });
      setEditingUnitId(null);
      setEditUnitName('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to rename unit');
    } finally {
      setUnitBusy(prev => ({ ...prev, [unitId]: false }));
    }
  };

  const handleDeleteUnit = async (unitId: number, lessonCount: number) => {
    if (lessonCount > 0) {
      alert('Cannot delete unit with lessons. Move or delete lessons first.');
      return;
    }
    if (!window.confirm('Delete this unit?')) return;
    try {
      setUnitBusy(prev => ({ ...prev, [unitId]: true }));
      await api.deleteUnit(unitId);
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to delete unit');
    } finally {
      setUnitBusy(prev => ({ ...prev, [unitId]: false }));
    }
  };

  const handleReorderUnit = async (unitId: number, direction: 'up' | 'down') => {
    try {
      setUnitBusy(prev => ({ ...prev, [unitId]: true }));
      await api.reorderUnit(unitId, direction);
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to reorder unit');
    } finally {
      setUnitBusy(prev => ({ ...prev, [unitId]: false }));
    }
  };

  const handleDelete = useCallback(async (lessonId: string) => {
    if (!lessonId) return;
    if (!window.confirm('Delete this lesson? This cannot be undone.')) return;
    try {
      setDeleting(lessonId);
      const res = await api.deleteLesson(lessonId);
      if (!res.success) {
        alert(res.error || 'Failed to delete lesson');
      }
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to delete lesson');
    } finally {
      setDeleting(null);
    }
  }, [loadCourse]);

  const loadChats = useCallback(async (lessonId: string) => {
    try {
      setChatsLoading(prev => ({ ...prev, [lessonId]: true }));
      const res = me?.authenticated && me?.user?.is_admin
        ? await api.getLessonChatsAdmin(lessonId)
        : await api.getLessonChats(lessonId);
      if (res.success) {
        const chats = (res.chats as ChatSummary[])
          .slice()
          .sort((a, b) => chatTimestamp(b.last_activity_at) - chatTimestamp(a.last_activity_at));
        setLessonChats(prev => ({ ...prev, [lessonId]: chats }));
      }
    } catch (e) {
      console.error('Failed to fetch chats for lesson', lessonId, e);
    } finally {
      setChatsLoading(prev => ({ ...prev, [lessonId]: false }));
    }
  }, [me]);

  const toggleChats = useCallback((lessonId: string) => {
    setExpandedLessons(prev => {
      const next = !prev[lessonId];
      if (next && !lessonChats[lessonId]) {
        void loadChats(lessonId);
      }
      return { ...prev, [lessonId]: next };
    });
  }, [lessonChats, loadChats]);

  const handleToggleVisibility = useCallback(async (lessonId: string, isPublic?: boolean) => {
    try {
      setToggling(prev => ({ ...prev, [lessonId]: true }));
      const res = await api.setLessonVisibility(lessonId, !isPublic);
      if (!res.success) {
        alert('Failed to update visibility');
        return;
      }
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to update visibility');
    } finally {
      setToggling(prev => ({ ...prev, [lessonId]: false }));
    }
  }, [loadCourse]);

  const handleMoveLesson = async (lessonId: string, targetUnitId: number) => {
    try {
      await api.moveLessonToUnit(lessonId, targetUnitId);
      setMovingLesson(null);
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to move lesson');
    }
  };

  // External link handlers
  const handleAddLink = async (unitId: number) => {
    if (!newLinkName.trim() || !newLinkUrl.trim()) return;
    try {
      setLinkBusy(prev => ({ ...prev, [unitId]: true }));
      await api.createExternalLink(unitId, newLinkName.trim(), newLinkUrl.trim());
      setAddingLinkToUnit(null);
      setNewLinkName('');
      setNewLinkUrl('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to create external link');
    } finally {
      setLinkBusy(prev => ({ ...prev, [unitId]: false }));
    }
  };

  const handleUpdateLink = async (linkId: number) => {
    if (!editLinkName.trim() || !editLinkUrl.trim()) return;
    try {
      setLinkBusy(prev => ({ ...prev, [linkId]: true }));
      await api.updateExternalLink(linkId, { name: editLinkName.trim(), url: editLinkUrl.trim() });
      setEditingLink(null);
      setEditLinkName('');
      setEditLinkUrl('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to update external link');
    } finally {
      setLinkBusy(prev => ({ ...prev, [linkId]: false }));
    }
  };

  const handleUpdateLessonOrder = async (lessonId: string) => {
    const orderVal = parseInt(editOrderValue, 10);
    if (isNaN(orderVal)) return;
    try {
      setToggling(prev => ({ ...prev, [lessonId]: true }));
      await api.updateLessonOrder(lessonId, orderVal);
      setEditingOrderLesson(null);
      setEditOrderValue('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to update lesson order');
    } finally {
      setToggling(prev => ({ ...prev, [lessonId]: false }));
    }
  };

  const handleUpdateLinkOrder = async (linkId: number) => {
    const orderVal = parseInt(editOrderValue, 10);
    if (isNaN(orderVal)) return;
    try {
      setLinkBusy(prev => ({ ...prev, [linkId]: true }));
      await api.updateExternalLink(linkId, { order_index: orderVal });
      setEditingOrderLink(null);
      setEditOrderValue('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to update link order');
    } finally {
      setLinkBusy(prev => ({ ...prev, [linkId]: false }));
    }
  };

  const handleDeleteLink = async (linkId: number) => {
    if (!window.confirm('Delete this external link?')) return;
    try {
      setLinkBusy(prev => ({ ...prev, [linkId]: true }));
      await api.deleteExternalLink(linkId);
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to delete external link');
    } finally {
      setLinkBusy(prev => ({ ...prev, [linkId]: false }));
    }
  };

  // Asset material handlers
  const handleAddAssetMaterial = async (unitId: number) => {
    const assetId = parseInt(newAssetMatAssetId, 10);
    if (!newAssetMatName.trim() || isNaN(assetId)) return;
    try {
      setAssetMatBusy(prev => ({ ...prev, [unitId]: true }));
      await api.createAssetMaterial(unitId, newAssetMatName.trim(), assetId);
      setAddingAssetMatToUnit(null);
      setNewAssetMatName('');
      setNewAssetMatAssetId('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to create asset material');
    } finally {
      setAssetMatBusy(prev => ({ ...prev, [unitId]: false }));
    }
  };

  const handleUpdateAssetMaterial = async (materialId: number) => {
    if (!editAssetMatName.trim()) return;
    try {
      setAssetMatBusy(prev => ({ ...prev, [materialId]: true }));
      await api.updateAssetMaterial(materialId, { name: editAssetMatName.trim() });
      setEditingAssetMat(null);
      setEditAssetMatName('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to update asset material');
    } finally {
      setAssetMatBusy(prev => ({ ...prev, [materialId]: false }));
    }
  };

  const handleUpdateAssetMatOrder = async (materialId: number) => {
    const orderVal = parseInt(editOrderValue, 10);
    if (isNaN(orderVal)) return;
    try {
      setAssetMatBusy(prev => ({ ...prev, [materialId]: true }));
      await api.updateAssetMaterial(materialId, { order_index: orderVal });
      setEditingOrderAssetMat(null);
      setEditOrderValue('');
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to update asset material order');
    } finally {
      setAssetMatBusy(prev => ({ ...prev, [materialId]: false }));
    }
  };

  const handleDeleteAssetMaterial = async (materialId: number) => {
    if (!window.confirm('Delete this asset material?')) return;
    try {
      setAssetMatBusy(prev => ({ ...prev, [materialId]: true }));
      await api.deleteAssetMaterial(materialId);
      await loadCourse();
    } catch (e: any) {
      alert(e?.message || 'Failed to delete asset material');
    } finally {
      setAssetMatBusy(prev => ({ ...prev, [materialId]: false }));
    }
  };

  const units = course?.units || [];
  const totalLessons = units.reduce((sum, u) => sum + u.lessons.length, 0);

  const renderLesson = (lesson: Lesson, unit: Unit) => (
    <div key={lesson.id} className="ml-item">
      <div className="ml-item-row">
        <div className="ml-item-order">
          {editingOrderLesson === lesson.id ? (
            <div className="order-edit">
              <input
                type="number"
                value={editOrderValue}
                onChange={(e) => setEditOrderValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateLessonOrder(lesson.id); if (e.key === 'Escape') { setEditingOrderLesson(null); setEditOrderValue(''); } }}
                autoFocus
                className="tm-order-input"
              />
              <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => handleUpdateLessonOrder(lesson.id)}>✓</button>
              <button className="tm-button tm-button--danger ml-delete-btn" onClick={() => { setEditingOrderLesson(null); setEditOrderValue(''); }}>✕</button>
            </div>
          ) : (
            <button
              className="order-badge"
              onClick={() => { setEditingOrderLesson(lesson.id); setEditOrderValue(String(lesson.order_index ?? 0)); }}
              title="Click to edit order"
            >
              {lesson.order_index ?? 0}
            </button>
          )}
        </div>
        <div className="ml-item-main">
          <div className="ml-title">
            {lesson.title}
            {!lesson.is_public && <span className="ml-private-badge">Private</span>}
          </div>
          <div className="ml-desc">{lesson.description}</div>
        </div>
        <div className="ml-item-controls">
          <button
            className="tm-button tm-button--primary ml-replace-btn"
            onClick={() => handleToggleVisibility(lesson.id, lesson.is_public)}
            disabled={!!toggling[lesson.id]}
            title={lesson.is_public ? 'Make this lesson private' : 'Make this lesson public'}
          >
            {toggling[lesson.id] ? '…' : (lesson.is_public ? '🔒' : '🔓')}
          </button>
          <button
            className="tm-button tm-button--primary ml-replace-btn"
            onClick={() => setMovingLesson({ lessonId: lesson.id, lessonTitle: lesson.title })}
            title="Move to another unit"
          >
            📦
          </button>
          <Link
            className="tm-button tm-button--primary ml-replace-btn"
            to={`/admin/lessons/${lesson.id}/preview`}
            title="Preview"
          >
            👁️
          </Link>
          <button
            className="tm-button tm-button--primary ml-replace-btn"
            onClick={() => toggleChats(lesson.id)}
            title="View chats"
          >
            💬
          </button>
          <button
            className="tm-button tm-button--danger ml-delete-btn"
            onClick={() => handleDelete(lesson.id)}
            disabled={deleting === lesson.id}
            title="Delete lesson"
          >
            {deleting === lesson.id ? '…' : '🗑️'}
          </button>
        </div>
      </div>
      {expandedLessons[lesson.id] && (
        <div className="ml-chats">
          <div className="ml-chats-header">
            <div className="ml-chats-title">Chats ({lessonChats[lesson.id]?.length ?? 0})</div>
            <button
              className="tm-button tm-button--primary ml-replace-btn ml-refresh-btn"
              onClick={() => void loadChats(lesson.id)}
              disabled={!!chatsLoading[lesson.id]}
            >
              {chatsLoading[lesson.id] ? '…' : '↻'}
            </button>
          </div>
          {chatsLoading[lesson.id] && <div className="ml-subtle">Loading chats…</div>}
          {!chatsLoading[lesson.id] && (
            (lessonChats[lesson.id]?.length ?? 0) === 0 ? (
              <div className="ml-empty">No chats yet.</div>
            ) : (
              <div className="ml-chat-list">
                {lessonChats[lesson.id]?.map((chat) => (
                  <div key={chat.id} className="ml-chat-item ml-chat-item--detailed">
                    <div className="ml-chat-main">
                      <div className="ml-chat-heading">
                        <div className="ml-chat-email">{chat.user_email || 'Unknown user'}</div>
                        <div className="ml-chat-badges">
                          {chat.rating && (
                            <span className={`ml-chat-rating ml-chat-rating--${chat.rating}`}>
                              {chat.rating === 'up' ? '👍 Thumbs up' : '👎 Thumbs down'}
                            </span>
                          )}
                          <span className="ml-chat-message-count">
                            {chat.message_count} {chat.message_count === 1 ? 'message' : 'messages'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-chat-dates">
                        <span><strong>Created:</strong> {formatChatTimestamp(chat.created_at)}</span>
                        <span><strong>Last activity:</strong> {formatChatTimestamp(chat.last_activity_at)}</span>
                        <span><strong>Completed:</strong> {formatChatTimestamp(chat.completed_at)}</span>
                      </div>
                    </div>
                    <Link className="ml-open-chat" to={`/lesson/${lesson.id}/${chat.id}`}>Open</Link>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container">
          <div className="page-intro">
            <h2>{course?.title || 'Manage Course'}</h2>
            <p>Edit course properties and manage units &amp; lessons</p>
          </div>
          {!(me?.authenticated && me?.user?.is_admin) && (
            <div className="admin-access-warning">
              <div className="tm-label">Admin access required</div>
              <div className="tm-mb-sm">You must be signed in as an admin to manage courses.</div>
              <div className="tm-actions">
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/login">Sign In</Link>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/">Back to Home</Link>
              </div>
            </div>
          )}

          {me?.authenticated && me?.user?.is_admin && (
            <>
              <div className="tm-mb-md">
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/admin/courses">← Back to Courses</Link>
              </div>

              {loading && <div className="loading">Loading…</div>}
              {error && <div className="error">{error}</div>}

              {course && (
                <>
                  {/* Course Properties Section */}
                  <div className="tm-surface admin-section">
                    <div className="admin-section-header">
                      <h3>Course Properties</h3>
                      {!editing && (
                        <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => setEditing(true)}>
                          ✏️ Edit
                        </button>
                      )}
                    </div>

                    {editing ? (
                      <div className="admin-form">
                        <div className="form-row">
                          <label>Title *</label>
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                          />
                        </div>
                        <div className="form-row">
                          <label>Description</label>
                          <input
                            type="text"
                            value={editForm.description}
                            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                          />
                        </div>
                        <div className="form-row">
                          <label>Detailed Description</label>
                          <textarea
                            value={editForm.detailed_description}
                            onChange={(e) => setEditForm(prev => ({ ...prev, detailed_description: e.target.value }))}
                            rows={3}
                          />
                        </div>
                        <div className="form-row-inline">
                          <div className="form-row">
                            <label>Subjects</label>
                            <input
                              type="text"
                              value={editForm.subjects}
                              onChange={(e) => setEditForm(prev => ({ ...prev, subjects: e.target.value }))}
                              placeholder="Comma-separated subjects"
                            />
                          </div>
                          <div className="form-row">
                            <label>Levels</label>
                            <input
                              type="text"
                              value={editForm.levels}
                              onChange={(e) => setEditForm(prev => ({ ...prev, levels: e.target.value }))}
                              placeholder="Comma-separated levels"
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <label>Tags</label>
                          <input
                            type="text"
                            value={editForm.tags}
                            onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                            placeholder="Comma-separated tags"
                          />
                        </div>
                        <label className="tm-check-label">
                          <input
                            type="checkbox"
                            checked={editForm.hidden}
                            onChange={(e) => setEditForm(prev => ({ ...prev, hidden: e.target.checked }))}
                          />
                          Hide this course from the public catalogue
                        </label>
                        <div className="form-actions">
                          <button className="tm-button tm-button--primary ml-replace-btn" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save Changes'}
                          </button>
                          <button className="tm-button tm-button--danger ml-delete-btn" onClick={() => {
                            setEditing(false);
                            setEditForm({
                              title: course.title,
                              description: course.description,
                              detailed_description: course.detailed_description,
                              subjects: (course.subjects || []).join(', '),
                              levels: (course.levels || []).join(', '),
                              tags: (course.tags || []).join(', '),
                              hidden: course.hidden,
                            });
                          }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin-props">
                        <p><strong>Description:</strong> {course.description || '(none)'}</p>
                        <p><strong>Detailed Description:</strong> {course.detailed_description || '(none)'}</p>
                        <p><strong>Subjects:</strong> {course.subjects?.join(', ') || '(none)'}</p>
                        <p><strong>Levels:</strong> {course.levels?.join(', ') || '(none)'}</p>
                        <p><strong>Tags:</strong> {course.tags?.join(', ') || '(none)'}</p>
                        <p>
                          <strong>Visibility:</strong>{' '}
                          {course.hidden ? (
                            <span className="course-hidden-badge">Hidden</span>
                          ) : 'Public'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Units & Lessons */}
                  <div className="tm-surface admin-section">
                    <div className="admin-section-header">
                      <h3>Units & Lessons ({totalLessons} lessons in {units.length} units)</h3>
                    </div>

                    {/* Add Unit Form */}
                    <div className="add-unit-form">
                      <input
                        type="text"
                        placeholder="New unit name…"
                        value={newUnitName}
                        onChange={(e) => setNewUnitName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddUnit()}
                      />
                      <button
                        className="tm-button tm-button--primary ml-replace-btn"
                        onClick={handleAddUnit}
                        disabled={addingUnit || !newUnitName.trim()}
                      >
                        {addingUnit ? 'Adding…' : '+ Add Unit'}
                      </button>
                    </div>

                    {units.length === 0 ? (
                      <div className="ml-empty">No units yet. Add a unit to start organizing lessons.</div>
                    ) : (
                      <div className="units-list">
                        {units.map((unit, unitIndex) => (
                          <div key={unit.id} className="unit-card">
                            <div className="unit-header">
                              {editingUnitId === unit.id ? (
                                <div className="unit-edit-form">
                                  <input
                                    type="text"
                                    value={editUnitName}
                                    onChange={(e) => setEditUnitName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleRenameUnit(unit.id)}
                                    autoFocus
                                  />
                                  <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => handleRenameUnit(unit.id)} disabled={unitBusy[unit.id]}>
                                    Save
                                  </button>
                                  <button className="tm-button tm-button--danger ml-delete-btn" onClick={() => { setEditingUnitId(null); setEditUnitName(''); }}>
                                    Cancel
                                  </button>
                              </div>
                              ) : (
                                <>
                                  <h4 className="unit-title">{unit.name}</h4>
                                  <span className="unit-lesson-count">{unit.lessons.length} lesson{unit.lessons.length !== 1 ? 's' : ''}</span>
                                </>
                              )}
                              <div className="unit-controls">
                                <button
                                  className="tm-button tm-button--primary ml-replace-btn"
                                  onClick={() => handleReorderUnit(unit.id, 'up')}
                                  disabled={unitIndex === 0 || unitBusy[unit.id]}
                                  title="Move up"
                                >
                                  ↑
                                </button>
                                <button
                                  className="tm-button tm-button--primary ml-replace-btn"
                                  onClick={() => handleReorderUnit(unit.id, 'down')}
                                  disabled={unitIndex === units.length - 1 || unitBusy[unit.id]}
                                  title="Move down"
                                >
                                  ↓
                                </button>
                                <button
                                  className="tm-button tm-button--primary ml-replace-btn"
                                  onClick={() => { setEditingUnitId(unit.id); setEditUnitName(unit.name); }}
                                  disabled={unitBusy[unit.id]}
                                  title="Rename"
                                >
                                  ✏️
                                </button>
                                <button
                                  className="tm-button tm-button--danger ml-delete-btn"
                                  onClick={() => handleDeleteUnit(unit.id, unit.lessons.length)}
                                  disabled={unitBusy[unit.id]}
                                  title={unit.lessons.length > 0 ? 'Move lessons first' : 'Delete unit'}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                            <div className="unit-lessons">
                              {unit.lessons.length === 0 && (unit.external_links?.length || 0) === 0 && (unit.exams?.length || 0) === 0 && (unit.asset_materials?.length || 0) === 0 ? (
                                <div className="ml-empty">No content in this unit. Upload lessons or add links.</div>
                              ) : (
                                <>
                                  {/* Interleaved unit content, sorted by order_index */}
                                  {(() => {
                                    const items: UnitItem[] = [
                                      ...unit.lessons.map(l => ({ type: 'lesson' as const, data: l })),
                                      ...(unit.external_links || []).map(l => ({ type: 'link' as const, data: l })),
                                      ...(unit.exams || []).map(e => ({ type: 'exam' as const, data: e })),
                                      ...(unit.asset_materials || []).map(m => ({ type: 'asset_material' as const, data: m }))
                                    ].sort((a, b) => {
                                      const aOrder = a.type === 'lesson' ? (a.data.order_index ?? 0) : a.data.order_index;
                                      const bOrder = b.type === 'lesson' ? (b.data.order_index ?? 0) : b.data.order_index;
                                      if (aOrder !== bOrder) return aOrder - bOrder;
                                      const aName = a.type === 'lesson' ? a.data.title : a.data.name;
                                      const bName = b.type === 'lesson' ? b.data.title : b.data.name;
                                      return compareNatural(aName, bName);
                                    });
                                    
                                    return items.map((item) => {
                                      if (item.type === 'lesson') {
                                        return renderLesson(item.data, unit);
                                      } else if (item.type === 'exam') {
                                        const exam = item.data as ExamSummary;
                                        return (
                                          <div key={`exam-${exam.id}`} className="ml-item exam-item">
                                            <div className="ml-item-row">
                                              <div className="ml-item-order">
                                                <span className="order-badge">{exam.order_index ?? 0}</span>
                                              </div>
                                              <div className="ml-item-main">
                                                <div className="ml-title">
                                                  <span className="exam-link-icon">Exam</span>
                                                  {exam.name}
                                                </div>
                                                <div className="ml-desc">Exam #{exam.id}</div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      } else if (item.type === 'asset_material') {
                                        const mat = item.data as AssetMaterial;
                                        return (
                                          <div key={`asset-mat-${mat.id}`} className="ml-item asset-material-item">
                                            <div className="ml-item-row">
                                              <div className="ml-item-order">
                                                {editingOrderAssetMat === mat.id ? (
                                                  <div className="order-edit">
                                                    <input
                                                      type="number"
                                                      value={editOrderValue}
                                                      onChange={(e) => setEditOrderValue(e.target.value)}
                                                      onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateAssetMatOrder(mat.id); if (e.key === 'Escape') { setEditingOrderAssetMat(null); setEditOrderValue(''); } }}
                                                      autoFocus
                                                      className="tm-order-input"
                                                    />
                                                    <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => handleUpdateAssetMatOrder(mat.id)}>✓</button>
                                                    <button className="tm-button tm-button--danger ml-delete-btn" onClick={() => { setEditingOrderAssetMat(null); setEditOrderValue(''); }}>✕</button>
                                                  </div>
                                                ) : (
                                                  <button
                                                    className="order-badge"
                                                    onClick={() => { setEditingOrderAssetMat(mat.id); setEditOrderValue(String(mat.order_index ?? 0)); }}
                                                    title="Click to edit order"
                                                  >
                                                    {mat.order_index ?? 0}
                                                  </button>
                                                )}
                                              </div>
                                              <div className="ml-item-main">
                                                {editingAssetMat?.id === mat.id ? (
                                                  <div className="link-edit-form">
                                                    <input
                                                      type="text"
                                                      placeholder="Display name"
                                                      value={editAssetMatName}
                                                      onChange={(e) => setEditAssetMatName(e.target.value)}
                                                    />
                                                    <div className="link-edit-actions">
                                                      <button
                                                        className="tm-button tm-button--primary ml-replace-btn"
                                                        onClick={() => handleUpdateAssetMaterial(mat.id)}
                                                        disabled={assetMatBusy[mat.id]}
                                                      >
                                                        Save
                                                      </button>
                                                      <button
                                                        className="tm-button tm-button--danger ml-delete-btn"
                                                        onClick={() => { setEditingAssetMat(null); setEditAssetMatName(''); }}
                                                      >
                                                        Cancel
                                                      </button>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <>
                                                    <div className="ml-title">
                                                      <span className="asset-material-icon">📥</span>
                                                      {mat.name}
                                                    </div>
                                                    <div className="ml-desc">
                                                      <a href={api.assetDownloadUrl(mat.asset_id)}>{mat.file_name}</a>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                              {editingAssetMat?.id !== mat.id && (
                                                <div className="ml-item-controls">
                                                  <button
                                                    className="tm-button tm-button--primary ml-replace-btn"
                                                    onClick={() => { setEditingAssetMat(mat); setEditAssetMatName(mat.name); }}
                                                    disabled={assetMatBusy[mat.id]}
                                                    title="Edit"
                                                  >
                                                    ✏️
                                                  </button>
                                                  <button
                                                    className="tm-button tm-button--danger ml-delete-btn"
                                                    onClick={() => handleDeleteAssetMaterial(mat.id)}
                                                    disabled={assetMatBusy[mat.id]}
                                                    title="Delete"
                                                  >
                                                    🗑️
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      } else {
                                        const link = item.data as ExternalLink;
                                        return (
                                          <div key={`link-${link.id}`} className="ml-item external-link-item">
                                            <div className="ml-item-row">
                                              <div className="ml-item-order">
                                                {editingOrderLink === link.id ? (
                                                  <div className="order-edit">
                                                    <input
                                                      type="number"
                                                      value={editOrderValue}
                                                      onChange={(e) => setEditOrderValue(e.target.value)}
                                                      onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateLinkOrder(link.id); if (e.key === 'Escape') { setEditingOrderLink(null); setEditOrderValue(''); } }}
                                                      autoFocus
                                                      className="tm-order-input"
                                                    />
                                                    <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => handleUpdateLinkOrder(link.id)}>✓</button>
                                                    <button className="tm-button tm-button--danger ml-delete-btn" onClick={() => { setEditingOrderLink(null); setEditOrderValue(''); }}>✕</button>
                                                  </div>
                                                ) : (
                                                  <button
                                                    className="order-badge"
                                                    onClick={() => { setEditingOrderLink(link.id); setEditOrderValue(String(link.order_index ?? 0)); }}
                                                    title="Click to edit order"
                                                  >
                                                    {link.order_index ?? 0}
                                                  </button>
                                                )}
                                              </div>
                                              <div className="ml-item-main">
                                                {editingLink?.id === link.id ? (
                                                  <div className="link-edit-form">
                                                    <input
                                                      type="text"
                                                      placeholder="Link name"
                                                      value={editLinkName}
                                                      onChange={(e) => setEditLinkName(e.target.value)}
                                                    />
                                                    <input
                                                      type="url"
                                                      placeholder="URL"
                                                      value={editLinkUrl}
                                                      onChange={(e) => setEditLinkUrl(e.target.value)}
                                                    />
                                                    <div className="link-edit-actions">
                                                      <button
                                                        className="tm-button tm-button--primary ml-replace-btn"
                                                        onClick={() => handleUpdateLink(link.id)}
                                                        disabled={linkBusy[link.id]}
                                                      >
                                                        Save
                                                      </button>
                                                      <button
                                                        className="tm-button tm-button--danger ml-delete-btn"
                                                        onClick={() => { setEditingLink(null); setEditLinkName(''); setEditLinkUrl(''); }}
                                                      >
                                                        Cancel
                                                      </button>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <>
                                                    <div className="ml-title">
                                                      <span className="external-link-icon">🔗</span>
                                                      {link.name}
                                                    </div>
                                                    <div className="ml-desc">
                                                      <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                              {editingLink?.id !== link.id && (
                                                <div className="ml-item-controls">
                                                  <button
                                                    className="tm-button tm-button--primary ml-replace-btn"
                                                    onClick={() => { setEditingLink(link); setEditLinkName(link.name); setEditLinkUrl(link.url); }}
                                                    disabled={linkBusy[link.id]}
                                                    title="Edit link"
                                                  >
                                                    ✏️
                                                  </button>
                                                  <button
                                                    className="tm-button tm-button--danger ml-delete-btn"
                                                    onClick={() => handleDeleteLink(link.id)}
                                                    disabled={linkBusy[link.id]}
                                                    title="Delete link"
                                                  >
                                                    🗑️
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      }
                                    });
                                  })()}
                                </>
                              )}
                              
                              {/* Add External Link Form */}
                              {addingLinkToUnit === unit.id ? (
                                <div className="add-link-form">
                                  <input
                                    type="text"
                                    placeholder="Link name"
                                    value={newLinkName}
                                    onChange={(e) => setNewLinkName(e.target.value)}
                                  />
                                  <input
                                    type="url"
                                    placeholder="https://..."
                                    value={newLinkUrl}
                                    onChange={(e) => setNewLinkUrl(e.target.value)}
                                  />
                                  <button
                                    className="tm-button tm-button--primary ml-replace-btn"
                                    onClick={() => handleAddLink(unit.id)}
                                    disabled={linkBusy[unit.id] || !newLinkName.trim() || !newLinkUrl.trim()}
                                  >
                                    Add
                                  </button>
                                  <button
                                    className="tm-button tm-button--danger ml-delete-btn"
                                    onClick={() => { setAddingLinkToUnit(null); setNewLinkName(''); setNewLinkUrl(''); }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="add-link-btn"
                                  onClick={() => setAddingLinkToUnit(unit.id)}
                                >
                                  + Add External Link
                                </button>
                              )}

                              {/* Add Asset Material Form */}
                              {addingAssetMatToUnit === unit.id ? (
                                <div className="add-link-form">
                                  <input
                                    type="text"
                                    placeholder="Display name"
                                    value={newAssetMatName}
                                    onChange={(e) => setNewAssetMatName(e.target.value)}
                                  />
                                  <select
                                    value={newAssetMatAssetId}
                                    onChange={(e) => setNewAssetMatAssetId(e.target.value)}
                                    className="tm-input tm-flex-fill"
                                  >
                                    <option value="">Select asset…</option>
                                    {courseAssets.map((a: any) => (
                                      <option key={a.id} value={a.id}>{a.file_name}</option>
                                    ))}
                                  </select>
                                  <button
                                    className="tm-button tm-button--primary ml-replace-btn"
                                    onClick={() => handleAddAssetMaterial(unit.id)}
                                    disabled={assetMatBusy[unit.id] || !newAssetMatName.trim() || !newAssetMatAssetId}
                                  >
                                    Add
                                  </button>
                                  <button
                                    className="tm-button tm-button--danger ml-delete-btn"
                                    onClick={() => { setAddingAssetMatToUnit(null); setNewAssetMatName(''); setNewAssetMatAssetId(''); }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="add-link-btn"
                                  onClick={() => setAddingAssetMatToUnit(unit.id)}
                                >
                                  + Add Asset Material
                                </button>
                              )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Move Lesson Modal */}
      {movingLesson && (
        <div className="modal-backdrop" onClick={() => setMovingLesson(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Move "{movingLesson.lessonTitle}"</h3>
              <button className="close" onClick={() => setMovingLesson(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Select target unit:</p>
              <div className="move-unit-list">
                {units.map(unit => (
                  <button
                    key={unit.id}
                    className="move-unit-option"
                    onClick={() => handleMoveLesson(movingLesson.lessonId, unit.id)}
                  >
                    {unit.name} ({unit.lessons.length} lessons)
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCoursePage;
